import { message } from '@whalecloud/fdx';
import { i18n } from 'fishx';
import Loadable from 'react-loadable';
import {
  queryUserPortlets,
  setDefaultLayout,
  queryUserLastOper,
  queryUserLastLogin,
  queryUserFavorites,
  saveDashboardLayout,
  queryDashboardLayout,
  getOSType,
  getBrowserType,
  getScreenType,
  queryMenuLayouts,
  saveMenuLayout,
  modMenuLayout,
  queryMenuPortlets,
} from '@/services/dashboard';

import { queryAdmin } from '@/services/api';
import { Reducer, Effect } from '@/typings/connect.d';

export interface DashboardStateType {
  lastOper?: [];
  lastLogin?: '';
  favorites?: [];
  layoutId?: any;
  allPortlets?: [];
  currentLayout?: any;
  OSTypes?: [],
  BrowserTypes?: [],
  ScreenTypes?: [],
}

export interface DashboardModelType {
  namespace: string;
  state: DashboardStateType;
  effects: {
    qryAdmin: Effect;
    qryLastOper: Effect;
    qryLastLogin: Effect;
    qryFavorites: Effect;
    qryPortlets: Effect;
    remoteLoadComponent: Effect;
    qryLayout: Effect;
    saveLayout: Effect;
    getOSType: Effect;
    getBrowserType: Effect;
    getScreenType: Effect;
    queryMenuLayouts: Effect;
    saveMenuLayout: Effect;
    modMenuLayout: Effect;
    queryMenuPortlets: Effect;
  };
  reducers: {
    save: Reducer<DashboardStateType>;
  };
}

const DashboardModel: DashboardModelType = {
  namespace: 'dxpDashboard',

  state: {
    lastOper: [],
    lastLogin: '',
    favorites: [],
    layoutId: null,
    allPortlets: [],
    currentLayout: null,
    OSTypes: [],
    BrowserTypes: [],
    ScreenTypes: [],
  },

  effects: {
    *qryAdmin(_, { call }) {
      const { data } = yield call(queryAdmin);
      return data;
    },
    *qryLastOper(_, { call, put }) {
      const { success, data } = yield call(queryUserLastOper);
      if (success) {
        yield put({
          type: 'save',
          payload: {
            lastOper: data,
          },
        });
      }
    },
    *qryLastLogin(_, { call, put }) {
      const { success, data } = yield call(queryUserLastLogin);
      if (success) {
        yield put({
          type: 'save',
          payload: {
            lastLogin: data,
          },
        });
      }
    },
    *qryFavorites({ payload }, { call, put }) {
      const { success, data } = yield call(queryUserFavorites, payload);
      if (success) {
        yield put({
          type: 'save',
          payload: {
            favorites: data,
          },
        });
      }
    },
    *qryPortlets({ payload }, { call, put }) {
      const { success, data } = yield call(queryUserPortlets, payload);
      if (success) {
        yield put({
          type: 'save',
          payload: {
            allPortlets: data.filter((v: any) => v.state === 'A'),
          },
        });
      }
    },
    /* eslint-disable-next-line */
    *remoteLoadComponent({ payload }) {
      const portlets = payload;
      /* eslint-disable no-plusplus */
      for (let index = 0; index < portlets.length; index++) {
        const item = portlets[index];
        if (item.url.includes('portalx-')) {
          // url中包含portalx-则认为是portalx提供的基本portlet，采用本地加载
          item.component = Loadable({
            loader: () => import('@/pages/Dashboard/components/' + item.url.split('-')[1]), // eslint-disable-line
            loading: require('@/components/Loading').default, // eslint-disable-line
          });
        }
      }
      return portlets;
    },
    *qryLayout({ payload }, { call, put }) {
      if (!payload) {
        return [];
      }
      const { success, data } = yield call(queryDashboardLayout, payload);
      if (success) {
        if (data.isDefault === 'Y') {
          if (!data.widgets) {
            return [];
          }
          const layout = JSON.parse(data.widgets);
          /* eslint-disable no-plusplus */
          for (let index = 0; index < layout.length; index++) {
            const item = layout[index];
            if (item.url) {
              if (item.url.includes('portalx-')) {
                // url中包含portalx-则认为是portalx提供的基本portlet，采用本地加载
                item.component = Loadable({
                  loader: () => import('@/pages/Dashboard/components/' + item.url.split('-')[1]), // eslint-disable-line
                  loading: require('@/components/Loading').default, // eslint-disable-line
                });
              }
            }
          }
          yield put({
            type: 'save',
            payload: {
              currentLayout: data,
              layoutId: data.layoutId,
            },
          });
          return layout;
        }
        return [];
      }
      return [];
    },
    *saveLayout({ payload }, { call, put, select }) {
      const { layoutId } = yield select((state: any) => state.dxpDashboard);
      const { success, data } = yield call(saveDashboardLayout, payload);
      if (success) {
        message.success(i18n('DASHBOARD_CUSTOMIZE_SUCCESS'));
        if (data.layoutId && layoutId !== data.layoutId) {
          yield call(setDefaultLayout, { portalId: payload.portalId, LayoutId: data.layoutId });
          yield put({
            type: 'save',
            payload: {
              layoutId: data.layoutId,
            },
          });
        }
      }
    },

    *getOSType(_, { call, put }) {
      const { success, data } = yield call(getOSType);
      if (success) {
        yield put({
          type: 'save',
          payload: {
            OSTypes: data,
          },
        });
        return data;
      }
      return null;
    },

    *getBrowserType(_, { call, put }) {
      const { success, data } = yield call(getBrowserType);
      if (success) {
        yield put({
          type: 'save',
          payload: {
            BrowserTypes: data,
          },
        });
        return data;
      }
      return null;
    },

    *getScreenType(_, { call, put }) {
      const { success, data } = yield call(getScreenType);
      if (success) {
        yield put({
          type: 'save',
          payload: {
            ScreenTypes: data,
          },
        });
        return data;
      }
      return null;
    },

    *queryMenuLayouts({ payload }, { call, put }) {
      if (!payload) {
        return [];
      }
      const { portalId, menuId } = payload;
      const { success, data } = yield call(queryMenuLayouts, portalId, menuId);
      if (success) {
        if (data) {
          const layout = JSON.parse(data.widgets);
          /* eslint-disable no-plusplus */
          for (let index = 0; index < layout.length; index++) {
            const item = layout[index];
            if (item.url) {
              if (item.url.includes('portalx-')) {
                // url中包含portalx-则认为是portalx提供的基本portlet，采用本地加载
                item.component = Loadable({
                  loader: () => import('@/pages/Dashboard/components/' + item.url.split('-')[1]), // eslint-disable-line
                  loading: require('@/components/Loading').default, // eslint-disable-line
                });
              }
            }
          }
          yield put({
            type: 'save',
            payload: {
              currentLayout: data,
              layoutId: data.layoutId,
            },
          });
          return layout;
        }
        return [];
      }
      return [];
    },

    *saveMenuLayout({ payload }, { call, put, select }) {
      const { layoutId } = yield select((state: any) => state.dxpDashboard);
      const { success, data } = yield call(saveMenuLayout, payload);
      if (success) {
        message.success(i18n('DYNAMIC_MENU_CUSTOMIZE_SUCCESS'));
        if (data.layoutId && layoutId !== data.layoutId) {
          yield put({
            type: 'save',
            payload: {
              layoutId: data.layoutId,
            },
          });
        }
      }
    },
    *modMenuLayout({ payload }, { call, put, select }) {
      const { layoutId } = yield select((state: any) => state.dxpDashboard);
      const { success, data } = yield call(modMenuLayout, payload);
      if (success) {
        message.success(i18n('DYNAMIC_MENU_MODIFY_SUCCESS'));
        if (data.layoutId && layoutId !== data.layoutId) {
          yield put({
            type: 'save',
            payload: {
              layoutId: data.layoutId,
            },
          });
        }
      }
    },

    *queryMenuPortlets({ payload }, { call, put }) {
      const { success, data } = yield call(queryMenuPortlets, payload);
      if (success) {
        yield put({
          type: 'save',
          payload: {
            allPortlets: data.filter((v: any) => v.state === 'A'),
          },
        });
      }
    },
  },

  reducers: {
    save(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },
};

export default DashboardModel;
